<template>
    <div>
        <div class="background-particles">
            <vue-particles
                class="background-particles"
                color="#E0C522"
                :particleOpacity="0.15"
                :particlesNumber="100"
                shapeType="circle"
                :particleSize="1"
                linesColor="#E0C522"
                :linesWidth="1"
                :lineLinked="true"
                :lineOpacity="0.15"
                :linesDistance="150"
                :moveSpeed="3"
                :hoverEffect="false"
                hoverMode="grab"
                :clickEffect="false"
                clickMode="push"
            >
            </vue-particles>
        </div>

        <div class="cover">
            <router-view v-slot="{ Component }">
                <transition v-if="loaded && !isLogin" name="show" appear mode="out-in" @before-enter="updateCurrentProfile">
                    <component :is="Component" />
                </transition>
            </router-view>

            <div v-if="isLogin">
                <LoginView @login-success="isLogin = false" />
            </div>

            <Transition v-if="!loaded && !isLogin" name="show" appear mode="out-in">
                <Loading />
            </Transition>
        </div>
    </div>
</template>

<script>
import { getAccounts } from '@/db/accountService';

import { ref, onMounted } from 'vue';

import { useMainStore } from '@/store/mainStore'
import { useRoute } from 'vue-router';
import { decryptMessage } from '@/services/cryptoUtils'
import { calculateFingerprint } from '@/services/fingerprint'

import ApiManager from '@/services/ApiManager';

import LoginView from '@/views/LoginView.vue';

import Loading from '@/components/Loading.vue';

import { useI18n } from 'vue-i18n';

function transformNotifications(data, t) {
    const months = [
        t('mounthes.january'),
        t('mounthes.february'),
        t('mounthes.march'),
        t('mounthes.april'),
        t('mounthes.may'),
        t('mounthes.june'),
        t('mounthes.july'),
        t('mounthes.august'),
        t('mounthes.september'),
        t('mounthes.october'),
        t('mounthes.november'),
        t('mounthes.december')
    ];

    const notificationsByMonth = {};

    if (!data?.notifications) {
        return notificationsByMonth;
    }

    data.notifications.forEach(notification => {
        const date = new Date(parseInt(notification.date) * 1000); // Перетворюємо дату
        const monthYear = `${months[date.getMonth()]} ${date.getFullYear()}`; // Формат: "місяць рік"

        if (!notificationsByMonth[monthYear]) {
            notificationsByMonth[monthYear] = []; // Створюємо масив для цього місяця
        }

        notificationsByMonth[monthYear].push(notification); // Додаємо сповіщення
    });

    // sort notifications by date
    for (const month in notificationsByMonth) {
        notificationsByMonth[month].sort((a, b) => {
            return b.date - a.date;
        });
    }

    return notificationsByMonth;
}

function transformNotificationsEng(data, t) {
    const months = [
        t('mounthes.january'),
        t('mounthes.february'),
        t('mounthes.march'),
        t('mounthes.april'),
        t('mounthes.may'),
        t('mounthes.june'),
        t('mounthes.july'),
        t('mounthes.august'),
        t('mounthes.september'),
        t('mounthes.october'),
        t('mounthes.november'),
        t('mounthes.december')
    ];

    const notificationsByMonth = {};

    if (!data?.notifications_eng) {
        return notificationsByMonth;
    }

    data.notifications_eng.forEach(notification => {
        const date = new Date(parseInt(notification.date) * 1000); // Перетворюємо дату
        const monthYear = `${months[date.getMonth()]} ${date.getFullYear()}`; // Формат: "місяць рік"

        if (!notificationsByMonth[monthYear]) {
            notificationsByMonth[monthYear] = []; // Створюємо масив для цього місяця
        }

        notificationsByMonth[monthYear].push(notification); // Додаємо сповіщення
    });

    // sort notifications by date
    for (const month in notificationsByMonth) {
        notificationsByMonth[month].sort((a, b) => {
            return b.date - a.date;
        });
    }

    return notificationsByMonth;
}

export default {
    name: 'BaseLayout',

    setup() {
        const store = useMainStore()

        const route = useRoute()

        const { t } = useI18n();

        const loaded = ref(false)

        const isLogin = ref(false)

        onMounted(async () => {
            loaded.value = false
            const currentRoute = route.path

            if (currentRoute === '/') {
                loaded.value = true
                return
            }
            const fingerprint = await calculateFingerprint()

            const accounts = await getAccounts()
            if (accounts.length === 0) {
                loaded.value = true
                isLogin.value = true
                return
            }

            let decryptedToken = ''
            const lastAccount = accounts[0]._data

            try {
                decryptedToken = await decryptMessage(lastAccount.token, fingerprint, lastAccount.iv)
            } catch (error) {
                console.error('Unable to decrypt saved account:', error.message);
                loaded.value = true
                isLogin.value = true
                return
            }

            const response = await ApiManager.getUserInfo(decryptedToken)
            const responseData = response.data

            if (responseData.status === false) {
                isLogin.value = true
                return
            } else {
                const clientInternetResponse = await ApiManager.getUserInternet(decryptedToken)
                const clientInternetData = clientInternetResponse.data

                const clientTvResponse = await ApiManager.getUserTv(decryptedToken)
                const clientTvData = clientTvResponse.data

                const allNotifications = await ApiManager.getAllNotifications(responseData.uid)

                const notificationsByMonth = transformNotifications(allNotifications.data, t)

                const notificationsByMonthEng = transformNotificationsEng(allNotifications.data, t)

                store.setCurrentNotificationsEng(notificationsByMonthEng)
                store.setCurrentNotifications(notificationsByMonth)

                store.setUserData(responseData)
                store.setUserInternetData(clientInternetData)
                store.setUserTvData(clientTvData)
            }

            loaded.value = true
        })

        const updateCurrentProfile = async () => {
            loaded.value = false

            const currentRoute = route.path

            if (isLogin.value) {
                loaded.value = true
                return
            }

            if (currentRoute === '/') {
                loaded.value = true
                return
            }
            if (store.userData == null) {
                const accounts = await getAccounts()

                if (accounts.length === 0) {
                    loaded.value = true

                    isLogin.value = true
                    return
                }
                const lastAccount = accounts[0]._data
                const fingerprint = await calculateFingerprint()
                let decryptedToken = ''
                try {
                    decryptedToken = await decryptMessage(lastAccount.token, fingerprint, lastAccount.iv)
                } catch (error) {
                    console.error('Unable to decrypt saved account:', error.message);
                    loaded.value = true
                    isLogin.value = true
                    return
                }

                const response = await ApiManager.getUserInfo(decryptedToken)

                const responseData = response.data
                store.setUserData(responseData)

                const clientInternetResponse = await ApiManager.getUserInternet(decryptedToken)
                const clientInternetData = clientInternetResponse.data

                const clientTvResponse = await ApiManager.getUserTv(decryptedToken)
                const clientTvData = clientTvResponse.data

                const allNotifications = await ApiManager.getAllNotifications(responseData.uid)

                const notificationsByMonth = transformNotifications(allNotifications.data, t)
                const notificationsByMonthEng = transformNotificationsEng(allNotifications.data, t)

                store.setCurrentNotificationsEng(notificationsByMonthEng)
                store.setCurrentNotifications(notificationsByMonth)

                store.setUserInternetData(clientInternetData)
                store.setUserTvData(clientTvData)

                isLogin.value = false
            }
            loaded.value = true
        }

        return {
            loaded,
            updateCurrentProfile,
            isLogin
        }
    },

    components: {
        Loading,
        LoginView
    }
}
</script>

<style lang="scss">
    .background-particles {
        // Decorative canvas must never intercept clicks on page controls.
        pointer-events: none;
        height: 100%;
        position: absolute;
        width: 100%;
        top: 0;
        left: 0;
    }

    @keyframes show-enter {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }

    }

    .show-enter-active {
        animation: show-enter 0.5s ease-out;
    }

    @keyframes show-leave {
        0% {
            opacity: 1;
        }

        100% {
            opacity: 0;
        }

    }

    .show-leave-active {
        animation: show-leave 0.5s ease-in;
    }

    .cover {
        max-width: 480px;
        margin: 0 auto;

        overflow-x: hidden;
    }
</style>
